import { Info } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";

import { TemplatePreviewPlaceholder } from "features/templates/components";
import SenderProfile from "features/templates/components/TemplatePreview/SenderProfile";
import {
  AIMFOX_TEMPLATE_PREVIEW_ACCOUNT,
  TEMPLATE_PREVIEW_ACCOUNTS,
  WHITE_LABEL_TEMPLATE_PREVIEW_ACCOUNT,
} from "features/templates/constants";
import type { TemplateSchemaType } from "features/templates/schemas";

import RenderIf from "common/components/RenderIf";
import Switch from "common/components/ui/Switch";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LinkedInProfile } from "common/types";

import PreviewContent from "./PreviewContent";
import PreviewHeader from "./PreviewHeader";

interface TemplatePreviewProps {
  updatedTemplate: TemplateSchemaType;
  previewTemplate: TemplateSchemaType | undefined;
  onUpdatePreview: () => void;
  senderProfiles: LinkedInProfile[];
  targetProfiles?: LinkedInProfile[];
  campaignData?: {
    campaignId: string;
    isTranslateEnabled: boolean;
    sourceLanguageCode: string;
  };
  hasError: boolean;
}

export default function TemplatePreview({
  updatedTemplate,
  previewTemplate,
  onUpdatePreview,
  targetProfiles,
  senderProfiles,
  campaignData = undefined,
  hasError,
}: TemplatePreviewProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [senderProfile, setSenderProfile] = useState(senderProfiles[0]);
  const [targetProfileIndex, setTargetProfileIndex] = useState(0);
  const [shouldShowTranslatedText, setShouldShowTranslatedText] =
    useState(true);

  if (senderProfiles.length === 0) {
    return <TemplatePreviewPlaceholder />;
  }

  // Get the template preview accounts based on the whitelabel customization
  const templatePreviewAccounts = [
    getWhitelabelCustomization()
      ? WHITE_LABEL_TEMPLATE_PREVIEW_ACCOUNT
      : AIMFOX_TEMPLATE_PREVIEW_ACCOUNT,
    ...TEMPLATE_PREVIEW_ACCOUNTS,
  ];

  // If the target profiles are not provided, use the template preview accounts
  const finalTargetProfiles = targetProfiles?.length
    ? targetProfiles
    : templatePreviewAccounts;

  const toggleTranslatedTextComponent = previewTemplate?.type !==
    "VOICE_TEMPLATE" && (
    <RenderIf
      condition={campaignData?.isTranslateEnabled && !!previewTemplate?.message}
    >
      <div className="flex items-center gap-1 px-1">
        <span className="text-xs-bold">Show translated</span>
        <Switch
          size="sm"
          checked={shouldShowTranslatedText}
          onCheckedChange={setShouldShowTranslatedText}
        />
      </div>
    </RenderIf>
  );

  return (
    <section
      className={clsx(
        "flex flex-1 flex-col overflow-hidden rounded-3xl border border-black-200 lg:basis-1/2",
        hasError && "mb-5",
      )}
    >
      <PreviewHeader
        targetProfiles={finalTargetProfiles}
        currentTargetIndex={targetProfileIndex}
        setCurrentTargetIndex={setTargetProfileIndex}
      />
      {!isDesktop && toggleTranslatedTextComponent}
      <div className="relative flex flex-1 flex-col overflow-hidden bg-white">
        <div
          className={clsx(
            "flex w-full items-center gap-2 px-3 pt-3 justify-between",
            senderProfiles.length === 1 ? "pb-3" : "pb-2",
          )}
        >
          <SenderProfile
            profileOptions={senderProfiles}
            senderProfile={senderProfile}
            setSenderProfile={setSenderProfile}
          />
          {isDesktop && toggleTranslatedTextComponent}
        </div>

        {previewTemplate?.type === "VOICE_TEMPLATE" ||
        updatedTemplate.type === "VOICE_TEMPLATE" ? (
          <div className="text-xs gap-x-1 flex items-center p-3">
            <Info size={16} weight="fill" /> Voice messages are not previewable
          </div>
        ) : (
          <PreviewContent
            previewTemplate={previewTemplate}
            updatedTemplate={updatedTemplate}
            targetProfileId={finalTargetProfiles[targetProfileIndex].id}
            senderProfileId={senderProfile ? senderProfile.id : undefined}
            onUpdatePreview={onUpdatePreview}
            campaignData={campaignData}
            shouldShowTranslatedText={shouldShowTranslatedText}
          />
        )}
      </div>
    </section>
  );
}
